import { useCallback, useEffect, useState } from "react";
import { Check, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, formatErr, downloadFile } from "@/lib/api";
import { toast } from "sonner";

const TYPE = { editor: "Grant editor", cpa: "CPA", attorney: "Attorney" };
const BADGE = { pending: "bg-amber-50 text-amber-700 border-amber-200", approved: "bg-emerald-50 text-emerald-700 border-emerald-200", declined: "bg-slate-100 text-slate-500 border-slate-200" };

function AppRow({ a, onDone }) {
  const [busy, setBusy] = useState(null);
  const act = async (kind) => {
    let body = {};
    if (kind === "decline") {
      const reason = window.prompt("Optional note to the applicant:", "");
      if (reason === null) return;
      body = { reason };
    }
    setBusy(kind);
    try { await api.post(`/admin/partner-applications/${a.application_id}/${kind}`, body); toast.success(kind === "approve" ? "Approved — pro account created" : "Application declined"); onDone(); }
    catch (e) { toast.error(formatErr(e)); setBusy(null); }
  };
  return (
    <div className="p-5 flex flex-col md:flex-row gap-4" data-testid={`partner-app-${a.application_id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-slate-900">{a.name}</span>
          <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border bg-slate-50 text-slate-600 border-slate-200">{TYPE[a.pro_type]}</span>
          <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border ${BADGE[a.status]}`} data-testid={`partner-app-status-${a.application_id}`}>{a.status}</span>
        </div>
        <div className="text-xs text-slate-500 mt-1">{a.email} · {a.phone} · {a.state} · {a.years_experience} yrs · Credential: {a.credential}</div>
        <p className="text-sm text-slate-600 mt-2 leading-relaxed line-clamp-3">{a.bio}</p>
        <div className="text-xs text-slate-500 mt-2">Signed "{a.signature}" on {new Date(a.signed_at).toLocaleString()} at <b className="text-slate-700">${a.rate} flat fee</b>{a.website && <> · <a href={a.website} target="_blank" rel="noreferrer" className="text-emerald-700 hover:underline">website</a></>}{a.lang === "es" && " · signed in Spanish"}</div>
        <div className="flex flex-wrap gap-3 items-center mt-2 text-xs">
          {a.referred_by_name && <span className="text-emerald-700" data-testid={`partner-app-referrer-${a.application_id}`}>Referred by {a.referred_by_name}</span>}
          <button onClick={() => downloadFile(`/admin/partner-applications/${a.application_id}/pdf`, `Partner_Agreement_${a.name.replace(/\W+/g, "_")}.pdf`).catch(() => toast.error("Couldn't download"))} className="text-slate-600 hover:text-slate-900 underline underline-offset-2" data-testid={`partner-app-pdf-${a.application_id}`}>Signed agreement PDF</button>
        </div>
      </div>
      {a.status === "pending" && (
        <div className="flex md:flex-col gap-2 shrink-0">
          <Button size="sm" onClick={() => act("approve")} disabled={!!busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`partner-approve-${a.application_id}`}>{busy === "approve" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}Approve</Button>
          <Button size="sm" variant="outline" onClick={() => act("decline")} disabled={!!busy} data-testid={`partner-decline-${a.application_id}`}><X className="h-4 w-4 mr-1" />Decline</Button>
        </div>
      )}
    </div>
  );
}

export function PartnerApplications() {
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState("pending");
  const load = useCallback(() => api.get("/admin/partner-applications", { params: status === "all" ? {} : { status } }).then((r) => setRows(r.data)), [status]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="bg-white border border-slate-200 rounded-lg" data-testid="partner-applications">
      <div className="p-4 flex flex-wrap gap-2 items-center justify-between border-b border-slate-100">
        <div className="flex gap-1">
          {["pending", "approved", "declined", "all"].map((s) => <button key={s} onClick={() => setStatus(s)} data-testid={`partner-filter-${s}`} className={`px-3 py-1 rounded-full text-xs border capitalize transition-colors ${status === s ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-600 hover:border-slate-500"}`}>{s}</button>)}
        </div>
        <a href="/partners" target="_blank" rel="noreferrer" className="text-xs text-emerald-700 font-semibold hover:underline" data-testid="partner-page-link">Share the partner sign-up page: /partners</a>
      </div>
      <div className="divide-y divide-slate-100 max-h-[520px] overflow-y-auto">
        {!rows ? <Loader2 className="h-5 w-5 m-5 animate-spin text-emerald-600" /> : rows.length === 0 ? <p className="p-6 text-sm text-slate-500" data-testid="partner-apps-empty">No {status === "all" ? "" : status} applications.</p> : rows.map((a) => <AppRow key={a.application_id} a={a} onDone={load} />)}
      </div>
    </div>
  );
}
